import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import ScrollToTop from '@/components/common/ScrollToTop';
import { useAuth } from '@/hooks/useAuth';

/* Pages are owned by another agent — lazy imported so this file compiles
   against whichever shape they ship. Each module is expected to have a
   default export of a React component. */
const Landing         = lazy(() => import('@/pages/Landing'));
const ForSeekers      = lazy(() => import('@/pages/ForSeekers'));
const ForHosts        = lazy(() => import('@/pages/ForHosts'));
const Auth            = lazy(() => import('@/pages/Auth'));
const Onboarding      = lazy(() => import('@/pages/Onboarding'));
const HostListings    = lazy(() => import('@/pages/HostListings'));
const SeekerListings  = lazy(() => import('@/pages/SeekerListings'));
// Tier 6 (2026-04-16): host listing management.
const HostMyListings  = lazy(() => import('@/pages/host/Listings'));
const HostListingEdit = lazy(() => import('@/pages/host/ListingEdit'));
const Requests        = lazy(() => import('@/pages/Requests'));
const Profile         = lazy(() => import('@/pages/Profile'));
const Terms           = lazy(() => import('@/pages/Terms'));
const Privacy         = lazy(() => import('@/pages/Privacy'));
const WhoWeAre        = lazy(() => import('@/pages/WhoWeAre'));
const AdminDashboard  = lazy(() => import('@/pages/admin/Dashboard'));
const AdminUsers      = lazy(() => import('@/pages/admin/Users'));
const AdminReports    = lazy(() => import('@/pages/admin/Reports'));
// Tier 7 (2026-04-16): exit-survey viewer.
const AdminDispositions = lazy(() => import('@/pages/admin/Dispositions'));
// Tier 8 (2026-04-16): feedback viewer.
const AdminFeedback   = lazy(() => import('@/pages/admin/Feedback'));
const AdminWhatsapp   = lazy(() => import('@/pages/admin/WhatsApp'));
const AdminSettings   = lazy(() => import('@/pages/admin/Settings'));
// Tier 9 (2026-04-16): shareable public pages (no auth, no profile gating).
const PublicListingPage = lazy(() => import('@/pages/public/Listing'));
const PublicSeekerPage  = lazy(() => import('@/pages/public/Seeker'));
const NotFoundPage    = lazy(() => import('@/pages/NotFound'));

function LoadingScreen() {
  // First-paint loader for lazy-loaded routes. The role+aria-label make this
  // announce as "Loading" to screen readers while the tab visibly settles.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
        padding: '48px 16px',
      }}
    >
      <span
        className="skeleton"
        aria-hidden="true"
        style={{ display: 'inline-block', width: 240, height: 24, borderRadius: 9999 }}
      />
    </div>
  );
}

export default function App() {
  // Bootstrap auth state (session me()) once per app.
  useAuth();

  useEffect(() => {
    // Ensure body has the right direction on first paint even before i18n fires the event.
    const dir = document.documentElement.dir || (document.documentElement.lang === 'en' ? 'ltr' : 'rtl');
    document.documentElement.dir = dir;
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/for-seekers" element={<ForSeekers />} />
            <Route path="/for-hosts" element={<ForHosts />} />
            <Route path="/displaced" element={<Navigate to="/for-seekers" replace />} />
            <Route path="/offer-home" element={<Navigate to="/for-hosts" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/who-we-are" element={<WhoWeAre />} />

            {/* Onboarding requires auth + no profile */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireNoProfile>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* Public browsing allowed per spec 01; page handles auth gating on contact actions */}
            <Route path="/hosts" element={<HostListings />} />

            {/* Seeker listings for hosts only */}
            <Route
              path="/seekers"
              element={
                <ProtectedRoute roles={['host', 'admin']} requireProfile>
                  <SeekerListings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/requests"
              element={
                <ProtectedRoute>
                  <Requests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            {/* Tier 6 (2026-04-16): host listing dashboard + editor. */}
            <Route
              path="/host/listings"
              element={
                <ProtectedRoute roles={['host', 'admin']} requireProfile>
                  <HostMyListings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/host/listings/new"
              element={
                <ProtectedRoute roles={['host', 'admin']} requireProfile>
                  <HostListingEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/host/listings/:id/edit"
              element={
                <ProtectedRoute roles={['host', 'admin']} requireProfile>
                  <HostListingEdit />
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dispositions"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminDispositions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminFeedback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/whatsapp"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminWhatsapp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />

            {/* Tier 9 (2026-04-16): shareable public pages by short reference
                code. No auth guard, no requireProfile — anyone with the link
                can read the anonymized payload. */}
            <Route path="/l/:ref" element={<PublicListingPage />} />
            <Route path="/s/:ref" element={<PublicSeekerPage />} />

            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
